import React, { useState, useEffect } from 'react';
import './App.css';
import { LanguageProvider } from './contexts/LanguageContext';
import Header from './components/Header';
import Hero from './components/Hero';
import FootballSchool from './components/FootballSchool';
import Enrollment from './components/Enrollment';
import ParentsTools from './components/ParentsTools';
import EmbedPage from './components/EmbedPage';
import LandingPage from './components/LandingPage';
import { NewsList, NewsArticle } from './components/NewsPage';
import { getNews } from './data/news';
import WhatsAppButton from './components/WhatsAppButton';
import { SCHEDULE_URL, TRANSPORT_URL } from './config/externalLinks';
import TrainingExperience from './components/TrainingExperience';
import FacebookPagePlugin from './components/FacebookPagePlugin';
import ValenciaPartnership from './components/ValenciaPartnership';
import Achievements from './components/Achievements';
import TeamSpirit from './components/TeamSpirit';
import Footer from './components/Footer';
import CupWinners from './components/CupWinners';
import CupDataAdmin from './components/CupDataAdmin';
import ComingSoon from './components/ComingSoon';

function App() {
  const pathname = window.location.pathname.replace(/\/+$/, '') || '/';
  const isEnrollment = pathname === '/upis';
  const isSchedule = pathname === '/raspored';
  const isTransport = pathname === '/prevoz';
  const newsMatch = pathname.match(/^\/vesti(?:\/([a-z0-9-]+))?$/);
  const isNewsList = Boolean(newsMatch) && !newsMatch![1];
  const newsItem = newsMatch && newsMatch[1] ? getNews(newsMatch[1]) : undefined;
  const isKids = pathname === '/fudbal-za-decu-novi-sad';
  const isClub = pathname === '/fudbalski-klub-novi-sad';
  const isSubPage = isEnrollment || isSchedule || isTransport || isKids || isClub || isNewsList || Boolean(newsItem);
  const [currentPage, setCurrentPage] = useState<string>(
    isEnrollment ? 'upis'
      : isSchedule ? 'raspored'
      : isTransport ? 'prevoz'
      : isKids ? 'kids'
      : isClub ? 'club'
      : isNewsList ? 'vesti'
      : newsItem ? 'vest'
      : 'home'
  );

  useEffect(() => {
    // Check URL hash for page routing
    const checkHash = () => {
      if (isSubPage) return;
      const hash = window.location.hash.replace('#', '');
      if (
        hash === 'junior-cup' ||
        hash === 'laliga-junior' ||
        hash === 'cup-scores' ||
        hash === 'cup-data'
      ) {
        setCurrentPage(hash);
        // Scroll to top when navigating to a new page
        window.scrollTo(0, 0);
      } else {
        setCurrentPage('home');
        if (hash && hash !== 'home') {
          // Sidro ka sekciji na početnoj (#skola-fudbala, #contact...) — sačekaj render pa skroluj
          requestAnimationFrame(() => {
            document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
        } else {
          window.scrollTo(0, 0);
        }
      }
    };

    checkHash();

    // Listen for hash changes
    const handleHashChange = () => {
      checkHash();
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Naslov stranice po ruti (Google i tabovi u pregledaču)
  useEffect(() => {
    const titles: Record<string, string> = {
      home: 'Škola fudbala Novi Sad | FK Junior – dečiji fudbal uz Valencia CF',
      upis: 'Upis u školu fudbala Novi Sad – cele godine, prvi treninzi besplatni | FK Junior',
      raspored: 'Raspored treninga | FK Junior Novi Sad',
      prevoz: 'Prevoz za decu na utakmice i turnire | FK Junior Novi Sad',
      kids: 'Fudbal za decu Novi Sad – škola fudbala FK Junior, U7–U14, prvi treninzi besplatni',
      club: 'FK Junior – fudbalski klub Novi Sad za decu, od 2006, partner Valencia CF',
      vesti: 'Vesti | FK Junior Novi Sad – škola fudbala',
      vest: newsItem ? `${newsItem.sr.title} | FK Junior Novi Sad` : 'Vesti | FK Junior Novi Sad',
      'junior-cup': 'Junior Cup 2026 Novi Sad – pobednici | FK Junior',
      'cup-scores': 'Junior Cup 2026 – rezultati | FK Junior',
      'laliga-junior': 'LaLiga Junior | FK Junior Novi Sad',
      'cup-data': 'Junior Cup – admin',
    };
    document.title = titles[currentPage] || titles.home;
    const descriptions: Record<string, string> = {
      home: 'FK Junior Novi Sad – škola fudbala i fudbalski klub za decu u Novom Sadu od 2006. Fudbal za decu od U7 do U14, dečaci i devojčice, licencirani treneri, tehnički partner Valencia CF. Prvi treninzi besplatni.',
      upis: 'Upis u školu fudbala FK Junior Novi Sad je otvoren tokom cele godine. Prvi treninzi su besplatni. Generacije U7–U14, treninzi 3 puta nedeljno, tehnički partner Valencia CF.',
      raspored: 'Nedeljni raspored treninga FK Junior Novi Sad po generacijama od U7 do U14, sa terminima i lokacijama.',
      prevoz: 'Organizacija prevoza dece FK Junior na utakmice i turnire. Roditelji nude ili traže mesto u autu.',
      kids: 'Fudbal za decu u Novom Sadu: FK Junior, dečaci i devojčice od 6 do 14 godina, 3 treninga nedeljno, licencirani treneri, metodologija Valencia CF. Upis cele godine, prvi treninzi besplatni.',
      club: 'FK Junior je fudbalski klub iz Novog Sada posvećen deci: osnovan 2006, 8 generacija od U7 do U14, 100+ dece, zvanični tehnički partner Valencia CF. Treninzi kod Sokolskog doma i na Najlonu.',
      vesti: 'Vesti iz FK Junior Novi Sad: utakmice, turniri, treninzi i život kluba.',
      vest: newsItem ? newsItem.sr.lead : 'Vesti iz FK Junior Novi Sad.',
    };
    const canonicals: Record<string, string> = {
      home: 'https://juniorfk.com/',
      upis: 'https://juniorfk.com/upis/',
      raspored: 'https://juniorfk.com/raspored/',
      prevoz: 'https://juniorfk.com/prevoz/',
      kids: 'https://juniorfk.com/fudbal-za-decu-novi-sad/',
      club: 'https://juniorfk.com/fudbalski-klub-novi-sad/',
      vesti: 'https://juniorfk.com/vesti/',
      vest: newsItem ? `https://juniorfk.com/vesti/${newsItem.slug}/` : 'https://juniorfk.com/vesti/',
    };
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', descriptions[currentPage] || descriptions.home);
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', canonicals[currentPage] || canonicals.home);
    const robots = document.querySelector('meta[name="robots"]');
    if (robots) robots.setAttribute('content', currentPage === 'cup-data' ? 'noindex, nofollow' : 'index, follow, max-image-preview:large');
  }, [currentPage, newsItem]);

  // Expose setCurrentPage to window for Header navigation
  useEffect(() => {
    (window as any).navigateToPage = (page: string) => {
      setCurrentPage(page);
      window.location.hash = page;
    };
  }, []);

  if (newsItem) {
    return (
      <LanguageProvider>
        <NewsArticle item={newsItem} />
        <WhatsAppButton />
      </LanguageProvider>
    );
  }

  if (isNewsList) {
    return (
      <LanguageProvider>
        <NewsList />
        <WhatsAppButton />
      </LanguageProvider>
    );
  }

  if (isKids || isClub) {
    return (
      <LanguageProvider>
        <LandingPage i18nKey={isKids ? 'landing.fudbalZaDecu' : 'landing.fudbalskiKlub'} />
        <WhatsAppButton />
      </LanguageProvider>
    );
  }

  if (isSchedule || isTransport) {
    return (
      <LanguageProvider>
        <EmbedPage
          i18nKey={isSchedule ? 'parents.schedule' : 'parents.transport'}
          src={isSchedule ? SCHEDULE_URL : TRANSPORT_URL}
        />
      </LanguageProvider>
    );
  }

  if (isEnrollment) {
    return (
      <LanguageProvider>
        <Enrollment />
        <WhatsAppButton />
      </LanguageProvider>
    );
  }

  if (currentPage === 'junior-cup' || currentPage === 'cup-scores') {
    return <CupWinners />;
  }

  if (currentPage === 'cup-data') {
    return (
      <LanguageProvider>
        <CupDataAdmin />
      </LanguageProvider>
    );
  }

  if (currentPage === 'laliga-junior') {
    return (
      <LanguageProvider>
        <ComingSoon />
      </LanguageProvider>
    );
  }

  return (
    <LanguageProvider>
      <div className="App App--cup">
        <Header />
        <section id="home">
          <Hero />
        </section>
        <FootballSchool />
        <ParentsTools />
        <ValenciaPartnership />
        <TrainingExperience />
        <FacebookPagePlugin />
        <Achievements />
        <TeamSpirit />
        <section id="contact">
          <Footer />
        </section>
      </div>
      <WhatsAppButton />
    </LanguageProvider>
  );
}

export default App;